import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Panel, Kicker } from "../ciWidgets";
import { ciApi } from "../ciApi";

const LENSES = [
  ["peers", "Peers"],
  ["industry", "Industry"],
  ["states", "State-wise"],
  ["regions", "Regional"],
  ["countries", "Country-wise"],
];

const QuadrantSVG = ({ lens }) => {
  const W = 880;
  const H = 560;
  const P = 50;
  const px = (v) => P + (v / 100) * (W - 2 * P);
  const py = (v) => H - P - (v / 100) * (H - 2 * P);
  const entities = lens.entities || [];
  // deterministic label collision offsetting (text-width aware, anchor-agnostic)
  const placed = [];
  const labelMeta = entities.map((e) => {
    const cx = px(e.x);
    const cy = py(e.y);
    const labelLeft = e.x > 74;
    const w = (e.name || "").length * 7.4 + 20;
    const x0 = labelLeft ? cx - 14 - w : cx + 14;
    const x1 = x0 + w;
    let dy = 0;
    for (let guard = 0; guard < 15; guard++) {
      const clash = placed.find((p) => x0 < p.x1 && x1 > p.x0 && Math.abs(p.y - (cy + dy)) < 16);
      if (!clash) break;
      dy = clash.y + 17 - cy;
    }
    placed.push({ x0, x1, y: cy + dy });
    return { cx, cy, labelLeft, dy };
  });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" data-testid="ci-quadrant-svg">
      <rect x={P} y={P} width={W - 2 * P} height={H - 2 * P} fill="#060d18" stroke="rgba(255,255,255,0.14)" />
      <rect x={(W) / 2} y={P} width={(W - 2 * P) / 2} height={(H - 2 * P) / 2} fill="rgba(45,212,191,0.05)" />
      <line x1={W / 2} y1={P} x2={W / 2} y2={H - P} stroke="rgba(255,255,255,0.14)" strokeDasharray="5 5" />
      <line x1={P} y1={H / 2} x2={W - P} y2={H / 2} stroke="rgba(255,255,255,0.14)" strokeDasharray="5 5" />
      {[
        ["STRONG EXECUTORS", P + 12, P + 22, "start"],
        ["LEADERS", W - P - 12, P + 22, "end"],
        ["EMERGING", P + 12, H - P - 12, "start"],
        ["VISION-LED", W - P - 12, H - P - 12, "end"],
      ].map(([t, x, y, anchor]) => (
        <text key={t} x={x} y={y} textAnchor={anchor} fill="rgba(255,255,255,0.28)" fontSize="12" fontWeight="700" letterSpacing="2">
          {t}
        </text>
      ))}
      <text x={W / 2} y={H - 14} textAnchor="middle" fill="rgba(45,212,191,0.75)" fontSize="12" fontWeight="700" letterSpacing="2">
        STRATEGIC VISION →
      </text>
      <text x={22} y={H / 2} textAnchor="middle" fill="rgba(45,212,191,0.75)" fontSize="11.5" fontWeight="700" letterSpacing="1.5" transform={`rotate(-90 22 ${H / 2})`}>
        EXECUTION STRENGTH →
      </text>
      {entities.map((e, i) => {
        const { cx, cy, labelLeft, dy } = labelMeta[i];
        const isSubject = e.type === "subject";
        return (
          <g key={i} data-testid={`ci-quadrant-dot-${i}`}>
            {isSubject && <circle cx={cx} cy={cy} r={13} fill="none" stroke="#d4a94e" strokeWidth="1.5" opacity="0.65" />}
            <circle cx={cx} cy={cy} r={isSubject ? 8 : 6} fill={isSubject ? "#d4a94e" : "#2dd4bf"} opacity={isSubject ? 1 : 0.85}>
              <title>{`${e.name} — Vision ${Math.round(e.x)} · Execute ${Math.round(e.y)}${e.rationale ? `\n${e.rationale}` : ""}`}</title>
            </circle>
            {dy !== 0 && <line x1={cx} y1={cy} x2={labelLeft ? cx - 11 : cx + 11} y2={cy + dy} stroke="rgba(255,255,255,0.25)" strokeWidth="0.8" />}
            <text
              x={labelLeft ? cx - 14 : cx + 14}
              y={cy + dy + 4}
              textAnchor={labelLeft ? "end" : "start"}
              fill={isSubject ? "#f2d9a4" : "rgba(255,255,255,0.75)"}
              fontSize={isSubject ? 13.5 : 12}
              fontWeight={isSubject ? 800 : 500}
            >
              {e.name}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export const QuadrantTab = ({ report, onReload }) => {
  const { id } = useParams();
  const [lensKey, setLensKey] = useState("peers");
  const [generating, setGenerating] = useState(false);
  const mq = report.market_quadrant;

  const generate = async () => {
    setGenerating(true);
    const prev = report.market_quadrant?.generated_at || null;
    try {
      await ciApi.post(`/assessments/${id}/quadrant/generate`);
      await onReload();
      toast.success("Leadership Quadrant generated");
    } catch (e) {
      for (let i = 0; i < 15; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const rep = await ciApi.get(`/assessments/${id}/report`);
          const ts = rep.data?.market_quadrant?.generated_at || null;
          if (ts && ts !== prev) {
            await onReload();
            toast.success("Leadership Quadrant generated");
            setGenerating(false);
            return;
          }
        } catch {}
      }
      toast.error(e.response?.data?.detail || "Quadrant generation failed — please retry");
    } finally {
      setGenerating(false);
    }
  };

  if (!mq) {
    return (
      <Panel className="p-12 text-center" data-testid="ci-tab-quadrant">
        <Sparkles className="w-8 h-8 text-teal-300 mx-auto mb-4" />
        <div className="font-serif text-2xl text-white mb-2">SAMANVAI Leadership Quadrant</div>
        <p className="text-[13px] text-white/55 max-w-xl mx-auto mb-6 leading-relaxed">
          Position this company on Execution Strength vs Strategic Vision — against its peers, the wider industry,
          state-level clusters, regional clusters and country benchmarks. New analyses generate this automatically;
          click below to generate it for this report.
        </p>
        <button
          onClick={generate}
          disabled={generating}
          data-testid="ci-quadrant-generate-btn"
          className="inline-flex items-center gap-2 rounded-full bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-[#04211c] font-bold text-sm px-7 py-3 transition-colors"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {generating ? "Positioning across 5 lenses… (~1 min)" : "Generate the Leadership Quadrant"}
        </button>
      </Panel>
    );
  }

  const available = LENSES.filter(([k]) => mq.lenses?.[k]);
  const lens = mq.lenses?.[lensKey] || mq.lenses?.[available[0]?.[0]];

  return (
    <div className="space-y-6" data-testid="ci-tab-quadrant">
      <Panel className="p-7">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <div>
            <Kicker>SAMANVAI Leadership Quadrant</Kicker>
            <div className="text-[12px] text-white/45 mt-1">
              Proprietary relative positioning — Execution Strength vs Strategic Vision
              {mq.primary_sector ? ` · Anchored to primary sector: ${mq.primary_sector}` : ""}
              {mq.home_country ? ` · Home country: ${mq.home_country}` : ""}
            </div>
          </div>
          <button
            onClick={generate}
            disabled={generating}
            data-testid="ci-quadrant-regenerate-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white/65 hover:bg-white/[0.06] disabled:opacity-50 font-bold text-[12px] px-4 py-2 transition-colors"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Regenerate
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {available.map(([k, label]) => (
            <button
              key={k}
              onClick={() => setLensKey(k)}
              data-testid={`ci-quadrant-filter-${k}`}
              className={`rounded-full px-4 py-1.5 text-[12px] font-bold border transition-colors ${
                lensKey === k
                  ? "bg-teal-400/15 border-teal-400/60 text-teal-300"
                  : "border-white/15 text-white/50 hover:text-white/80 hover:border-white/30"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {lens ? (
          <>
            {lens.summary && (
              <div className="rounded-lg border border-teal-400/20 bg-teal-400/[0.04] px-4 py-3 mb-5 text-[13px] text-white/75 leading-relaxed" data-testid="ci-quadrant-summary">
                {lens.summary}
              </div>
            )}
            <QuadrantSVG lens={lens} />
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-2.5 mt-6">
              {(lens.entities || []).map((e, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span
                    className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${e.type === "subject" ? "bg-amber-300" : "bg-teal-400/80"}`}
                  />
                  <div>
                    <span className={`text-[12.5px] font-bold ${e.type === "subject" ? "text-amber-200" : "text-white/85"}`}>
                      {e.name}
                    </span>
                    <span className="text-[11px] text-white/40 ml-2">
                      Vision {Math.round(e.x)} · Execute {Math.round(e.y)}
                    </span>
                    {e.rationale && <div className="text-[11.5px] text-white/50 leading-snug">{e.rationale}</div>}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-[13px] text-white/50">This lens is not available for the current analysis.</div>
        )}
      </Panel>
    </div>
  );
};
